import { useEffect, useRef, useState } from "react";
import { Camera, ImageIcon, Loader2, RefreshCw, Trash2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useJourney } from "@/lib/site";
import { api, errMsg } from "@/lib/api";
import { SLOTS } from "@/lib/options";
import { StepNav } from "./IntentStep";

let seq = 0;

const Tile = ({ item, onDelete, onReplace }) => (
  <div className="relative h-24 w-28 overflow-hidden rounded-md border border-border bg-slate-100" data-testid={`photo-tile-${item.slot}`}>
    {item.preview ? <img src={item.preview} alt={`${item.slot} preview`} className="h-full w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
      : <div className="flex h-full items-center justify-center text-slate-400"><ImageIcon className="h-6 w-6" /></div>}
    {item.status === "uploading" && (
      <div className="absolute inset-x-0 bottom-0 bg-white/90 px-1.5 py-1 text-[11px] font-semibold">
        <div className="h-1 rounded bg-slate-200"><div className="h-1 rounded bg-primary transition-[width]" style={{ width: `${item.progress}%` }} /></div>
        <span data-testid="photo-upload-progress">{item.progress < 100 ? `Uploading ${item.progress}%` : "Processing…"}</span>
      </div>
    )}
    {item.status === "error" && <div className="absolute inset-0 flex items-center justify-center bg-red-50/90 p-1 text-center text-[11px] font-semibold text-red-800"><AlertCircle className="mr-1 h-3 w-3 shrink-0" />Failed</div>}
    {item.status !== "uploading" && (
      <div className="absolute right-1 top-1 flex gap-1">
        {item.status === "done" && <button type="button" onClick={onReplace} className="rounded bg-white/95 p-1.5 shadow-sm" aria-label="Replace photo" data-testid="photo-replace-button"><RefreshCw className="h-3.5 w-3.5" /></button>}
        <button type="button" onClick={onDelete} className="rounded bg-white/95 p-1.5 shadow-sm" aria-label="Remove photo" data-testid="photo-delete-button"><Trash2 className="h-3.5 w-3.5 text-red-700" /></button>
      </div>
    )}
  </div>
);

export const PhotosStep = ({ next, back, target, slots = SLOTS, compact = false, onBusy }) => {
  const j = useJourney();
  const draft = target ? { id: "x" } : j.state.draft;
  const base = target ? target.base : `/public/drafts/${draft?.id}/photos`;
  const hdrs = target ? {} : { "X-Draft-Token": draft?.token };
  const [items, setItems] = useState(() => ((target ? target.initial : j.state.photos) || []).map((p) => ({ ...p, key: `s${seq++}`, status: "done", progress: 100 })));
  const [errors, setErrors] = useState({});
  const [warn, setWarn] = useState(null);
  const inputs = useRef({});
  const replacing = useRef(null);
  useEffect(() => { onBusy?.(items.some((i) => i.status === "uploading")); }, [items, onBusy]);

  const patch = (key, p) => setItems((list) => list.map((i) => (i.key === key ? { ...i, ...p } : i)));
  const sync = (list) => (target ? target.onChange?.(list) : j.update({ photos: list.filter((i) => i.status === "done").map(({ id, slot, name }) => ({ id, slot, name })) }));

  const upload = async (file, slot) => {
    const key = `u${seq++}`;
    const preview = URL.createObjectURL(file);
    setItems((l) => [...l, { key, slot, name: file.name, preview, status: "uploading", progress: 0 }]);
    const fd = new FormData();
    fd.append("slot", slot);
    fd.append("file", file);
    try {
      const { data } = await api.post(base, fd, {
        headers: hdrs, timeout: 180000,
        onUploadProgress: (e) => e.total && patch(key, { progress: Math.round((e.loaded / e.total) * 100) }),
      });
      setItems((l) => { const n = l.map((i) => (i.key === key ? { ...i, id: data.id, status: "done", progress: 100 } : i)); sync(n); return n; });
    } catch (e) {
      patch(key, { status: "error" });
      setErrors((x) => ({ ...x, [slot]: errMsg(e, "That photo couldn't be uploaded. Please try again.") }));
    }
  };

  const remove = async (item) => {
    if (item.id) await api.delete(`${base}/${item.id}`, { headers: hdrs }).catch(() => {});
    setItems((l) => { const n = l.filter((i) => i.key !== item.key); sync(n); return n; });
  };

  const onFiles = async (slot, files) => {
    setErrors((x) => ({ ...x, [slot]: null }));
    const list = Array.from(files || []);
    if (replacing.current) { const old = replacing.current; replacing.current = null; await remove(old); }
    for (const f of list) {
      if (f.size > 20 * 1024 * 1024) { setErrors((x) => ({ ...x, [slot]: "That photo is larger than 20 MB." })); continue; }
      upload(f, slot);
    }
  };

  const onNext = () => {
    if (items.some((i) => i.status === "uploading")) return setWarn("Please wait for your photos to finish uploading.");
    next();
  };

  if (!draft) return <p className="py-10 text-slate-500" data-testid="photos-preparing"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Preparing photo upload…</p>;
  return (
    <section data-testid="photos-step">
      {!compact && <>
      <h1 className="text-2xl font-extrabold text-slate-900 sm:text-3xl">Add photos of your car</h1>
      <p className="mt-2 text-slate-700">Photos help us review your car properly. Take them now with your phone camera or choose from your gallery. You can still continue without photos if you have trouble uploading.</p>
      </>}
      <div className={`${compact ? "mt-3" : "mt-6"} divide-y divide-border rounded-md border border-border bg-white`}>
        {slots.map((s) => {
          const mine = items.filter((i) => i.slot === s.id);
          const canAdd = s.multiple || mine.length === 0;
          return (
            <div key={s.id} className="p-4" data-testid={`photo-slot-${s.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0"><p className="font-semibold text-slate-900">{s.label}</p><p className="text-sm text-slate-600">{s.hint}</p></div>
                {canAdd && (
                  <Button type="button" variant="outline" onClick={() => inputs.current[s.id]?.click()} data-testid={`photo-add-${s.id}`}>
                    <Camera className="mr-1 h-4 w-4" /> {mine.length ? "Add another" : "Add photo"}
                  </Button>
                )}
              </div>
              <input ref={(el) => { inputs.current[s.id] = el; }} type="file" accept="image/*,.heic,.heif" multiple={!!s.multiple} className="sr-only" tabIndex={-1}
                     aria-label={`Upload ${s.label} photo`} onChange={(e) => { onFiles(s.id, e.target.files); e.target.value = ""; }} data-testid={`photo-input-${s.id}`} />
              {mine.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {mine.map((i) => <Tile key={i.key} item={i} onDelete={() => remove(i)} onReplace={() => { replacing.current = i; inputs.current[s.id]?.click(); }} />)}
                </div>
              )}
              {errors[s.id] && <p className="mt-2 text-sm font-medium text-destructive" role="alert" data-testid={`photo-error-${s.id}`}>{errors[s.id]}</p>}
            </div>
          );
        })}
      </div>
      {warn && <p className="mt-4 text-sm font-medium text-destructive" role="alert">{warn}</p>}
      {!target && !compact && <StepNav back={back} onNext={onNext} nextLabel={items.some((i) => i.status === "done") ? "Continue" : "Continue without photos"} />}
    </section>
  );
};
